import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, switchMap, mergeMap, first, tap} from 'rxjs/operators';
import { of } from 'rxjs';
import * as StoragesActions from '../actions/storages.actions';
import { StoragesService } from '@app/services/storages/storage.service';
import { Store } from '@ngrx/store';
import { CategoriesService } from '@app/services/categories/categories.service';
import { AreasService } from '@app/services/areas/areas.service';
import { selectStoragesDirectionState, selectStoragesSortState } from '../selectors/storages.selectors';
import { ToastrService } from 'ngx-toastr';
import * as CoreActions from '@app/core/store/core.action';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Location } from '@angular/common';

@Injectable()
export class StoragesEffects {

  loadStorages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.loadStorages),
      switchMap(action => {
        return this._storages.getStorages('name', 'asc', action.query).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/storages'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }

            return StoragesActions.loadStoragesSuccess({ storages: data.storages, total: data.total })
          }),
          catchError(error => {
            return of(StoragesActions.loadStoragesFailure({ error }))
          })
        )
      })
    );
  });

  loadStoragesPath$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.loadStoragesPath),
      switchMap(action => {
        return this._storages.getStoragesPath(action.storageStructureID, action.locationID).pipe(
          map((data): any => {
            if(data.result!=null && data.result!='undefined' && data.result=='failed.access' && this.location.path()=='/storages'){
              this.store$.dispatch(CoreActions.showPermissionMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.access')
              }));
            }
            return StoragesActions.loadStoragesPathSuccess({ storagesPath: data.storages })
          }),
          catchError(error => {
            return of(StoragesActions.loadStoragesPathFailure({ error }))
          })
        )
      })
    );
  });







  searchStorages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.search),
      switchMap(action => {
        let direction: string;
        let sort: string;
        // this.store$.select(selectStoragesDirectionState).pipe(first()).subscribe(res => direction = res);
        // this.store$.select(selectStoragesSortState).pipe(first()).subscribe(res => sort = res);

        return this._storages.searchStorages('name', 'ASC', action.query).pipe(
          map((data): any => {
            return StoragesActions.searchSuccess({ storages: data.storages, total: data.total })
          }),
          catchError(error => {
            return of(StoragesActions.searchFailure({ error }))
          })
        )
      })
    );
  });

  createStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.saveStorage),
      switchMap(action => {
        return this._storages.onSave(action.form).pipe(
          map((response): any => {
            if(response.result!='undefined' && response.result=='failed.max'){
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.limitstorage'),
                show_upgrade: true
              }));
              return of(StoragesActions.saveStorageFailure({ error: 'An error occurred.' }))
            }

            if(response.error && response.message!=''){
              //return StoragesActions.saveStorageSuccess(response)
              this._toastr.error(this._translation.instant(response.message));
              return StoragesActions.saveStorageFailure({ error: 'An error occurred.' })
            }
            else{
              return StoragesActions.saveStorageSuccess(response)
            }
          }),
          catchError(error => {
            return of(StoragesActions.saveStorageFailure({ error }))
          })
        )
      })
    );
  });

  updateStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.updateStorage),
      switchMap(action => {
        return this._storages.updateStorage(action.form).pipe(
          map((response): any => {
            if(response.error && response.message!=''){
              this._toastr.error(this._translation.instant(response.message));
              return StoragesActions.updateStorageFailure({ error: 'An error occurred.' })
            }

            return StoragesActions.updateStorageSuccess(response)
          }),
          catchError(error => {
            return of(StoragesActions.updateStorageFailure({ error }))
          })
        )
      })
    );
  });

  loadStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.loadStorage),
      switchMap(action => {
        return this._storages.getStorage(action.storageStructureID).pipe(
          map(({ storage }): any => {
            return StoragesActions.loadStorageSuccess({ storage })
          }),
          catchError(error => {
            return of(StoragesActions.loadStorageFailure({ error }))
          })
        )
      })
    );
  });

  deleteStorages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.deleteStorages),
      switchMap(action => {
        return this._storages.batchDelete(action.selected).pipe(
          map(() => {
            return StoragesActions.deleteStoragesSuccess(action.query);}),
          catchError(error => {
            return of(StoragesActions.deleteStoragesFailure({ error }))
          })
          )
      })
    );
  });

  refreshStoragesAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.deleteStoragesSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => StoragesActions.loadStorages({query:''})),
          catchError(error => of(StoragesActions.deleteStoragesFailure({ error }))
          ))
      })
    );
  });

  cleanStoragesAfterDelete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.deleteStoragesSuccess),
      switchMap(action => {
        return of(0).pipe(
          map(() => StoragesActions.setSelected({ selected: [] })),
          catchError(error => of(StoragesActions.deleteStoragesFailure({ error }))
          ))
      })
    );
  });

  deleteStorage$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.deleteStorage),
      mergeMap(action => {
        return this._storages.onDelete(action.storageStructureID).pipe(
          tap(() => {
            // this._toastr.error(`Successfully deleted storage.`);
          }),
          map((): any => {
            return StoragesActions.deleteStorageSuccess({ storageStructureID: action.storageStructureID })
          }),
          catchError(error => {
            if (error?.error?.message === 'cannotDeleteStorageWithProducts') {
              this.store$.dispatch(CoreActions.showErrorMessage({
                title: this._translation.instant('errormessage.warning'),
                body: this._translation.instant('errormessage.cannotDeleteStorageWithProducts'),
                show_upgrade: false
              }));
            }
            return of(StoragesActions.deleteStorageFailure({ error: error.message, storageStructureID: action.storageStructureID }))
          })
        )
      })
    );
  });


  setSort$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StoragesActions.setSort),
      switchMap(action => {
        return this._storages.getStorages(action.sort, action.direction, action.query).pipe(
          map((data): any => {
            return StoragesActions.loadStoragesSuccess({ storages: data.storages, total: data.total })
          })
        )
      })
    );
  });

  constructor(
    private actions$: Actions, private _storages: StoragesService,
    private store$: Store, private _categories: CategoriesService,
    private _areas: AreasService,
    private _toastr: ToastrService,
    public _router: Router,
    private location : Location,
    public _translation: TranslateService
  ) { }

}
